import React from 'react';
import { useNode, useEditor, useEditingCond } from '@splashthat/splash-cms-engine';
import { FocusScope } from '@react-aria/focus';
import PropTypes from 'prop-types';

import Close from 'common/icons/CloseForm';
import { Modal } from 'common/components/TouchpointEditor/contexts/ModalManager';
import { NODE_ID_ADD_SESSION_TO_CALENDAR } from 'common/compounds/AgendaTouchpoint/components/constants';
import {
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_SUBHEADLINE_TEXT,
    BRAND_LIBRARY_BODY_TEXT,
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_HOVER_COLOR,
} from 'common/constants/brandLibrary';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useBootstrap } from 'utils/bootstrap/useBootstrap';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';

import CalendarAttachment from 'common/components/TouchpointEditor/components/TouchpointNodes/CalendarAttachment';
import {
    AddToCalendarModalGlobalStyles,
    AddToCalendarModalWrapper,
    AddToCalendarModalContainer,
    CloseButtonContainer,
} from './AddToCalendarModal.styles';
import { getAgendaCalendarProviders } from './CalendarProviders';

const BASE_CLASS_NAME = 'AddToCalendarModal';

export const AddToCalendarModal = ({ styles, meta, descendants, selected, hovered }) => {
    const { theme, event } = useTouchpointData();

    const editor = useEditor((state) => ({ editing: state.editing }));
    const onCloseCallbackIfOnEditorMode = useEditingCond(() => editor.actions.unselectAllNodes());
    const node = useNode();
    const customCssClassName = getCustomCssClassName(node);
    const { guestAccessHash } = useGuestAccessHash(`sessionsRegistration/${event.id}/guestAccessHash`);
    const bootstrapData = useBootstrap(['app.splashApiUrl']);
    const apiUrl = bootstrapData['app.splashApiUrl'];

    return (
        <Modal modalId={NODE_ID_ADD_SESSION_TO_CALENDAR}>
            {({ isOpen, onResolve, eventId, session }) => {
                const shouldShowModal = isOpen || selected;
                const onClose = onCloseCallbackIfOnEditorMode || onResolve;
                const preventModalClickFromClosing = (e) => e.stopPropagation();

                if (!shouldShowModal) {
                    return null;
                }

                return (
                    <AddToCalendarModalContainer className={BASE_CLASS_NAME} onClick={onClose}>
                        <AddToCalendarModalGlobalStyles isEditing={editor.editing} />
                        <FocusScope contain={!editor.editing} restoreFocus autoFocus>
                            <AddToCalendarModalWrapper
                                data-qa={`${BASE_CLASS_NAME}__modal-wrapper`}
                                $backgroundColor={theme.colors[descendants.modal.styles.backgroundColor]}
                                role="dialog"
                                aria-modal="true"
                                onClick={preventModalClickFromClosing}
                                className={customCssClassName}
                            >
                                <CloseButtonContainer
                                    $color={theme.colors[descendants.closeButton.styles.color]}
                                    $colorHover={theme.colors[descendants.closeButton.styles.colorHover]}
                                    onClick={onClose}
                                    data-qa={BASE_CLASS_NAME}
                                    aria-label="Close"
                                >
                                    <Close />
                                </CloseButtonContainer>
                                <CalendarAttachment
                                    providers={getAgendaCalendarProviders({
                                        session,
                                        eventId,
                                        apiUrl,
                                        guestAccessHash,
                                    })}
                                    styles={styles}
                                    meta={meta}
                                    descendants={descendants}
                                    hovered={hovered}
                                    selected={selected}
                                />
                            </AddToCalendarModalWrapper>
                        </FocusScope>
                    </AddToCalendarModalContainer>
                );
            }}
        </Modal>
    );
};

AddToCalendarModal.splash = {
    related: {
        settings: React.lazy(() => import('./AddToCalendarModal.tools')),
    },
    defaultProps: {
        styles: {},
        meta: {
            displayName: 'Calendar Attachment Modal',
            visible: true,
        },
        descendants: {
            modal: {
                styles: {
                    backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                },
            },
            closeButton: {
                styles: {
                    color: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    colorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                },
            },
            headline: {
                styles: {
                    fontSize: 14,
                    fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    fontFamily: BRAND_LIBRARY_SUBHEADLINE_TEXT,
                },
                meta: {
                    content: 'Add to your calendar',
                },
            },
            providerLogoIcons: {
                styles: {
                    color: null,
                    colorHover: null,
                },
            },
            providerLogoNames: {
                styles: {
                    fontSize: 14,
                    fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    fontFamily: BRAND_LIBRARY_BODY_TEXT,
                },
            },
        },
    },
};

AddToCalendarModal.propTypes = {
    styles: PropTypes.object.isRequired,
    meta: PropTypes.shape({
        content: PropTypes.string,
        visible: PropTypes.bool,
    }).isRequired,
    descendants: PropTypes.object.isRequired,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
};
